import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';
import { DeviceIdService } from '@src/app/areas/device/services/device-id.service';
import { RegionService } from '@app/areas/auth/services/region.service';
import { LocalizeService } from '@shared/services/helpers/localize/localize.service';
import { ErrorBreadcrumbsService } from './error-breadcrumbs.service';

const LEGAL_ENTITY_STORAGE_KEY = 'selectedLegalEntityId';
const WORKSPACE_STORAGE_KEY = 'selectedWorkspaceId';

@Injectable({ providedIn: 'root' })
export class SupportCaseMetadataService {
  constructor(
    private readonly deviceIdService: DeviceIdService,
    private readonly regionService: RegionService,
    private readonly localizeService: LocalizeService,
    private readonly errorBreadcrumbs: ErrorBreadcrumbsService,
    @Optional() private readonly router?: Router,
  ) {}

  build(): Record<string, string> {
    const metadata: Record<string, string> = {};

    this.assign(metadata, 'device_id', this.deviceIdService.get());
    this.assign(metadata, 'platform', 'web');
    this.assign(metadata, 'device', 'browser');
    this.assign(metadata, 'app_version', environment.appVersion ?? '');
    this.assign(metadata, 'locale', this.localizeService.currentLocaleValue?.trim());
    this.assign(metadata, 'region', this.regionService.get());
    this.assign(metadata, 'organisation_id', this.readStorage(LEGAL_ENTITY_STORAGE_KEY));
    this.assign(metadata, 'workspace_id', this.readStorage(WORKSPACE_STORAGE_KEY));
    this.assign(metadata, 'viewport_width', this.getViewportWidth());
    this.assign(metadata, 'viewport_height', this.getViewportHeight());
    this.assign(metadata, 'timezone', this.getTimeZone());

    // Context & Diagnostics
    const currentRoute = this.getCurrentRoute();
    this.assign(metadata, 'current_route', currentRoute);
    this.assign(metadata, 'patient_id', this.extractPatientId(currentRoute));
    this.assign(metadata, 'client_timestamp', new Date().toISOString());

    const recentErrors = this.errorBreadcrumbs.getRecentErrors();
    if (recentErrors.length > 0) {
      this.assign(metadata, 'recent_http_errors', JSON.stringify(recentErrors));
    }

    return metadata;
  }

  private getCurrentRoute(): string {
    if (this.router?.url) {
      return this.router.url;
    }
    if (typeof window !== 'undefined') {
      return window.location.pathname + window.location.search;
    }
    return '';
  }

  private extractPatientId(url: string): string | null {
    if (!url) return null;
    // Check path like /patients/view/:id or /patients/:id
    const match = url.match(/\/patients\/(?:view\/)?([a-zA-Z0-9_-]+)/i);
    if (match?.[1] && match[1] !== 'new' && match[1] !== 'list') {
      return match[1];
    }
    // Check query param patientId
    try {
      const parsed = new URL(url, 'http://localhost');
      return parsed.searchParams.get('patientId') || parsed.searchParams.get('patient') || null;
    } catch {
      return null;
    }
  }

  private assign(metadata: Record<string, string>, key: string, value: string | null | undefined): void {
    const normalized = `${value ?? ''}`.trim();
    if (!normalized) {
      return;
    }

    metadata[key] = normalized;
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private getViewportWidth(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.innerWidth > 0 ? `${window.innerWidth}` : null;
  }

  private getViewportHeight(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.innerHeight > 0 ? `${window.innerHeight}` : null;
  }

  private getTimeZone(): string | null {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
      return null;
    }
  }
}
